import { useEffect, useMemo, useState } from 'react';
import { displayName, type BookingDto } from '@bingo/shared';
import { api } from '../lib/api';
import { activeLang, useT } from '../lib/i18n';
import { addDays, todayIso } from '../lib/dates';
import { TransferTime } from '../components/TransferTime';

export type TransferView = 'day' | 'week' | 'month';

const pad = (n: number) => String(n).padStart(2, '0');
const parts = (s: string) => s.split('-').map(Number) as [number, number, number];
/** 0 = Monday. */
const weekday = (s: string) => {
  const [y, m, d] = parts(s);
  return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
};
export const mondayOf = (s: string) => addDays(s, -weekday(s));
export const firstOfMonth = (s: string) => `${s.slice(0, 7)}-01`;
export function addMonths(s: string, n: number): string {
  const [y, m] = parts(s);
  const k = y * 12 + (m - 1) + n;
  return `${Math.floor(k / 12)}-${pad((k % 12) + 1)}-01`;
}
const lastOfMonth = (s: string) => addDays(addMonths(s, 1), -1);
const locale = () => (activeLang === 'fr' ? 'fr-FR' : 'en-GB');
const fmt = (s: string, o: Intl.DateTimeFormatOptions) => {
  const [y, m, d] = parts(s);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(locale(), { ...o, timeZone: 'UTC' });
};

/** Title of the period shown in the date bar. */
export function periodTitle(view: TransferView, date: string, t: ReturnType<typeof useT>): string {
  if (view === 'month') return fmt(date, { month: 'long', year: 'numeric' });
  const s = mondayOf(date);
  return t('Week of {date}', { date: fmt(s, { day: 'numeric', month: 'short', year: 'numeric' }) });
}

/** Moves the anchor date one period back or forward. */
export function stepPeriod(view: TransferView, date: string, step: number): string {
  if (view === 'month') return addMonths(date, step);
  return addDays(date, view === 'week' ? 7 * step : step);
}

const ARROW = { arrival: '↘', departure: '↗', intercity: '↔' } as const;

/**
 * Week and month views of the Transfers page (Bruno, 18/10): every transfer of the period by day,
 * with its time, client, pax and vehicle. A click on a day opens that day's board.
 */
export function TransfersRange({ view, date, onDay, onOpen }: {
  view: 'week' | 'month'; date: string; onDay: (d: string) => void; onOpen: (b: BookingDto) => void;
}) {
  const t = useT();
  const today = todayIso();
  const [from, to] = view === 'week' ? [mondayOf(date), addDays(mondayOf(date), 6)] : [mondayOf(firstOfMonth(date)), addDays(mondayOf(lastOfMonth(date)), 6)];
  const [data, setData] = useState<{ bookings: BookingDto[]; vehicles: Record<string, number> } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setData(null);
    api<{ bookings: BookingDto[]; vehicles: Record<string, number> }>(`/api/bookings/transfers?from=${from}&to=${to}`)
      .then((r) => alive && setData(r))
      .catch((e) => alive && setError((e as Error).message));
    return () => { alive = false; };
  }, [from, to]);

  const days = useMemo(() => {
    const out: string[] = [];
    for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
    return out;
  }, [from, to]);
  const byDay = useMemo(() => {
    const m = new Map<string, BookingDto[]>();
    for (const b of data?.bookings ?? []) m.set(b.date, [...(m.get(b.date) ?? []), b]);
    return m;
  }, [data]);

  const month = date.slice(0, 7);
  const inPeriod = (data?.bookings ?? []).filter((b) => view === 'week' || b.date.startsWith(month));
  const toPlace = inPeriod.filter((b) => !b.dayVehicleId).length;

  if (error) return <div className="alert alert-bad">{error}</div>;
  if (!data) return <div className="muted">{t('Loading…')}</div>;

  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="kpis">
        <div className="kpi"><b>{inPeriod.length}</b><span>{t('transfers')}</span></div>
        <div className="kpi"><b>{inPeriod.reduce((s, b) => s + b.pax, 0)}</b><span>{t('pax')}</span></div>
        <div className={`kpi ${toPlace ? 'kpi-warn' : ''}`}><b>{toPlace}</b><span>{t('to place')}</span></div>
      </div>
      <div className={`trr ${view === 'week' ? 'trr-week' : 'trr-month'}`}>
        {view === 'month' && [0, 1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="trr-wd muted">{fmt(addDays(from, i), { weekday: 'short' })}</div>
        ))}
        {days.map((d) => {
          const list = byDay.get(d) ?? [];
          const out = view === 'month' && !d.startsWith(month);
          const unplaced = list.filter((b) => !b.dayVehicleId).length;
          const shown = view === 'month' ? list.slice(0, 4) : list;
          return (
            <div key={d} className={`card trr-day ${out ? 'out' : ''} ${d === today ? 'today' : ''}`}>
              <button type="button" className="trr-head" onClick={() => onDay(d)} title={t('Open this day')}>
                <b>{view === 'week' ? fmt(d, { weekday: 'short', day: 'numeric' }) : Number(d.slice(8))}</b>
                {list.length > 0 && <span className="muted">{list.length} · {list.reduce((s, b) => s + b.pax, 0)} {t('pax')}</span>}
              </button>
              {unplaced > 0 && <span className="trr-warn">{unplaced === 1 ? t('1 to place') : t('{n} to place', { n: unplaced })}</span>}
              <ul className="trr-list">
                {shown.map((b) => {
                  const v = b.dayVehicleId ? data.vehicles[b.dayVehicleId] : undefined;
                  return (
                    <li key={b.id}>
                      <button type="button" onClick={() => onOpen(b)} title={`${b.pickupPlace ?? '?'} → ${b.dropoffPlace ?? '?'}${b.transportRef ? ` · ${b.transportRef}` : ''}`}>
                        <span className="trr-t"><TransferTime b={b} /></span>
                        <span className={`trr-dir tr-${b.transferDirection}`} aria-label={b.transferDirection ?? ''}>{ARROW[b.transferDirection!]}</span>
                        <span className="trr-n ellipsis">{displayName(b) || b.reference}</span>
                        <span className="trr-p muted">{b.pax}</span>
                        {view === 'week' && <span className={`trr-v ${v ? 'today-tr-v' : 'today-tr-v none'}`}>{v ? `V${v}` : '–'}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
              {list.length > shown.length && (
                <button type="button" className="trr-more" onClick={() => onDay(d)}>{t('+{n} more', { n: list.length - shown.length })}</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
